import { Callout, Position } from '@blueprintjs/core'
import React, { FC, useEffect } from 'react'
import Select from 'react-select'
import { UprightStyle } from '../../../../../types'
import { Spinner } from '../../../../../components/Spinner'
import { useDepopChannelFormContext } from '../../../../products_v4/listings/depop/DepopChannelFormContext'
import { useDepopSizes } from './useDepopSizes'
import { GutterTooltipError } from '../GutterTooltipError'
import { ProductFormGroup } from '../ProductFormGroup'

const styles: UprightStyle = {
  zeroMarginBottom: { marginBottom: 0 }
}

interface Props {
  depopSizes: ReturnType<typeof useDepopSizes>
  required?: boolean
  error?: any
  errorTooltipOffset?: number
  errorTooltipPosition?: Position
}

// Not DepopSpecificSelect: that one is string-valued for specific slugs, while a size is a numeric id
// that only means anything next to its size set, so both are written together.
export const SizeSelect: FC<Props> = ({
  depopSizes,
  required,
  error,
  errorTooltipOffset,
  errorTooltipPosition = Position.LEFT
}) => {
  const { form, onListingChange, setLocalErrors } = useDepopChannelFormContext()
  const { sizeSet, loadedFor, loading, error: fetchError } = depopSizes

  const hasSizes = !!sizeSet.sizeSetId && sizeSet.sizes.length > 0
  // Only act once the size set in hand belongs to the category currently selected.
  const settled = !loading && !fetchError && loadedFor === form.categoryMarketCategoryId

  // Drop a size held over from another category. Gated on `settled` so a saved size is never
  // discarded mid-load or on a transient failure.
  useEffect(() => {
    if (!settled) return

    if (!hasSizes) {
      if (form.sizeSetId || form.sizeId) {
        onListingChange({ name: 'sizeSetId', value: null })
        onListingChange({ name: 'sizeId', value: null })
      }
      return
    }

    // Matching the id alone is not enough: ids repeat across sets with different meanings (4 is "M"
    // in mens-top-sizes, "US 8" in mens-shoe-sizes), so the set has to match too.
    const belongsToThisCategory =
      form.sizeSetId === sizeSet.sizeSetId && sizeSet.sizes.some(size => size.value === form.sizeId)

    if (form.sizeId && !belongsToThisCategory) {
      onListingChange({ name: 'sizeSetId', value: null })
      onListingChange({ name: 'sizeId', value: null })
    }
  }, [settled, hasSizes, sizeSet, form.sizeId, form.sizeSetId, onListingChange])

  // useLocalValidation is driven by field changes and cannot know a size is mandatory until the set
  // has loaded, so this owns the error instead. Only gates v4 New/Edit; DepopCard passes a no-op
  // setLocalErrors. A failed fetch clears rather than asserts, so it cannot lock out saving.
  useEffect(() => {
    const invalid = settled && hasSizes && !form.sizeId

    setLocalErrors(previousErrors => {
      if (invalid) return { ...previousErrors, depopListingSize: true }
      const { depopListingSize: _removed, ...rest } = previousErrors || {}
      return rest
    })
  }, [settled, hasSizes, form.sizeId, setLocalErrors])

  // Leave no error behind if the Depop section goes away while a size is missing.
  useEffect(
    () => () =>
      setLocalErrors(previousErrors => {
        const { depopListingSize: _removed, ...rest } = previousErrors || {}
        return rest
      }),
    [setLocalErrors]
  )

  if (fetchError) return <Callout intent="warning">{fetchError}</Callout>
  if (loading) return <Spinner />
  if (!hasSizes) return null

  return (
    <ProductFormGroup
      title="Size"
      error={error}
      showRequiredLabel={required}
      style={styles.zeroMarginBottom}
      className="nuke-top-margin-loadable-select"
    >
      <GutterTooltipError isOpen={!!error} offsetPx={errorTooltipOffset} position={errorTooltipPosition}>
        <Select
          value={form.sizeId}
          options={sizeSet.sizes.map(size => ({ value: size.value, label: size.displayName }))}
          simpleValue
          onChange={v => {
            const sizeId = v === null || v === '' ? null : Number(v)
            onListingChange({ name: 'sizeSetId', value: sizeId === null ? null : sizeSet.sizeSetId })
            onListingChange({ name: 'sizeId', value: sizeId })
          }}
          placeholder="Select"
          className="react-select-same-height-as-blueprint"
        />
      </GutterTooltipError>
    </ProductFormGroup>
  )
}
